import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
} from '@mui/material';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import billingService, { type Subscription, type Invoice, type Plan } from '../services/billingService';
import addonAssignmentService, { type AddonSeatSummary } from '../services/addonAssignmentService';
import { getApiErrorInfo } from '../services/api';
import { useUserContext } from '../hooks/useUserContext';
import { IndividualPlanSelectionPanel } from '../pages/billing/IndividualPlans';
import { BusinessPlanSelectionPanel } from '../pages/billing/BusinessPlans';
import ActiveModuleManager from './billing/ActiveModuleManager';
import TalkToSalesDialog from './billing/TalkToSalesDialog';
import { subscriptionChipSx, type SubscriptionTone } from '../utils/subscriptionStatus';

// Mirrors IndividualBillingManagement.tsx — keep aligned so both pages share the same type voice.
const TYPE = {
  display:   { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
  subheading:{ fontSize: '1.125rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
  bodyStrong:{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
  body:      { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
  caption:   { fontSize: '0.75rem',  fontWeight: 500, lineHeight: 1.4 },
  overline:  { fontSize: '0.75rem',  fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const, lineHeight: 1.4 },
} as const;

/** Normalize billingPeriod values like "1", "month", "year" into readable text */
function formatBillingCycle(billingPeriod: string, billingPeriodUnit?: number): string {
  const p = billingPeriod.toLowerCase();
  if (p === 'year' || p === 'yearly' || p === 'annual') return 'year';
  if (p === 'month' || p === 'monthly') return 'month';
  if (billingPeriodUnit === 1 && (p === '1' || p === '12')) return p === '12' ? 'year' : 'year';
  if (p === '1') return 'year';
  return billingPeriod;
}

interface BillingManagementProps {
  tenantId: string;
}

export default function BillingManagement({ tenantId }: BillingManagementProps) {
  const { isTenantAdmin } = useUserContext();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [addonSeatSummaries, setAddonSeatSummaries] = useState<AddonSeatSummary[]>([]);
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId]);

  const loadData = async () => {
    if (!tenantId) {
      setError('No tenant ID available. Please ensure you are logged in.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const subscriptionData = await billingService.getSubscription(tenantId);
      setSubscription(subscriptionData);

      if (subscriptionData) {
        const [invoicesData, seatSummaries] = await Promise.all([
          billingService.getInvoices(tenantId),
          addonAssignmentService.getSeatSummary(tenantId).catch(() => [] as AddonSeatSummary[]),
        ]);
        setInvoices(invoicesData);
        setAddonSeatSummaries(seatSummaries);
      } else {
        setInvoices([]);
        setAddonSeatSummaries([]);
      }
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      if (apiError.status === 404) {
        setSubscription(null);
        setInvoices([]);
      } else if (apiError.isNetworkError) {
        setError('Unable to connect to the service. Please try again or contact support if the problem persists.');
      } else {
        setError(apiError.message || 'Failed to load billing information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setActionLoading(true);
      await billingService.cancelSubscription(tenantId, { endOfTerm: true });
      setCancelDialogOpen(false);
      await loadData();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to cancel subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setActionLoading(true);
      await billingService.reactivateSubscription(tenantId);
      await loadData();
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to reactivate subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    try {
      setActionLoading(true);
      const session = await billingService.getBillingPortalSession(tenantId);
      window.open(session.portalUrl, '_blank', 'noopener,noreferrer');
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to open billing portal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleIndividualCheckout = async (planId: string, addonIds: string[], _billingPeriod: 'monthly' | 'annual') => {
    try {
      setActionLoading(true);
      sessionStorage.setItem('checkout_flow', 'billing');
      const redirectUrl = `${window.location.origin}/subscription-confirmation`;
      const page = await billingService.createUserSubscriptionCheckout({ planId, addonIds, redirectUrl });
      window.location.href = page.url;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to create checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBusinessCheckout = async (
    basePlanId: string,
    addonIds: string[],
    _billingPeriod: 'monthly' | 'annual',
    personCount: number,
    _selectedAddons: Plan[],
    addonQuantities: Record<string, number>
  ) => {
    try {
      setActionLoading(true);
      sessionStorage.setItem('checkout_flow', 'billing');
      const redirectUrl = `${window.location.origin}/subscription-confirmation`;
      const page = await billingService.createSubscriptionCheckout(tenantId, {
        planId: basePlanId,
        addonIds,
        quantity: personCount,
        addonQuantities,
        redirectUrl,
      });
      window.location.href = page.url;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to create checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const handleModuleChanges = async (addonIds: string[], seatCounts: Record<string, number>) => {
    if (!subscription) return;
    try {
      setActionLoading(true);
      sessionStorage.setItem('checkout_flow', 'billing');
      const redirectUrl = `${window.location.origin}/subscription-confirmation`;
      const page = await billingService.createTenantManageCheckout(tenantId, {
        addonIds,
        addonQuantities: seatCounts,
        redirectUrl,
      });
      window.location.href = page.url;
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setError(apiError.message || 'Failed to create checkout');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active': return 'Active';
      case 'in_trial': return 'Free Trial';
      case 'cancelled': return 'Expired';
      case 'non_renewing': return 'Auto-Renew Off';
      case 'paused': return 'Paused';
      default: return status.replace('_', ' ');
    }
  };

  const getStatusTone = (status: string): SubscriptionTone => {
    switch (status.toLowerCase()) {
      case 'active': return 'success';
      case 'in_trial': return 'info';
      case 'cancelled': return 'error';
      case 'non_renewing': return 'warning';
      default: return 'neutral';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={4} sx={{ maxWidth: 920 }}>
      {/* Header — matches the tenant-admin section header voice (theme h4) */}
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Billing & Subscription
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: '65ch' }}>
          Manage your organization&rsquo;s plan, billing, and module seats.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Subscription card ── */}
      {subscription ? (
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header — plan identity + status */}
          <Box sx={{ px: { xs: 2.5, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: 3 }}>
            <Box>
              <Typography sx={{ ...TYPE.overline, color: 'primary.main', display: 'block', mb: 0.75 }}>
                Organization Plan
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ ...TYPE.display, color: 'text.primary', wordBreak: 'break-word' }}>
                  {subscription.planName}
                </Typography>
                <Chip
                  label={getStatusLabel(subscription.status)}
                  size="small"
                  sx={[subscriptionChipSx(getStatusTone(subscription.status)), { ...TYPE.caption, fontWeight: 600, height: 24 }]}
                />
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* Details grid */}
          <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0 }}>
              {/* Billing amount — comped/negotiated plans have a $0 base price, so
                  lead with what the plan includes instead of a misleading "$0.00". */}
              <Box sx={{ pr: { sm: 3 } }}>
                <Typography sx={{ ...TYPE.overline, color: 'text.secondary' }}>
                  Billing
                </Typography>
                {subscription.planAmount === 0 ? (
                  <>
                    <Typography sx={{ ...TYPE.display, mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                      {(() => {
                        const totalSeats = addonSeatSummaries.reduce((sum, a) => sum + a.purchasedQuantity, 0);
                        return totalSeats > 0 ? `${totalSeats} seats` : 'Custom';
                      })()}
                    </Typography>
                    <Typography sx={{ ...TYPE.caption, color: 'text.secondary' }}>
                      billed under your organization&rsquo;s agreement
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography sx={{ ...TYPE.display, mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(subscription.planAmount, subscription.currency)}
                    </Typography>
                    <Typography sx={{ ...TYPE.caption, color: 'text.secondary' }}>
                      per {formatBillingCycle(subscription.billingPeriod, subscription.billingPeriodUnit)}
                    </Typography>
                  </>
                )}
              </Box>

              {/* Mobile divider */}
              <Divider sx={{ my: 2, display: { xs: 'block', sm: 'none' } }} />

              {/* Next billing date */}
              <Box
                sx={{
                  px: { sm: 3 },
                  borderLeft: { xs: 'none', sm: '1px solid' },
                  borderColor: { sm: 'divider' },
                }}
              >
                <Typography sx={{ ...TYPE.overline, color: 'text.secondary' }}>
                  Next Renewal
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                  <CalendarIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography sx={{ ...TYPE.bodyStrong, fontVariantNumeric: 'tabular-nums' }}>
                    {formatDate(subscription.nextBillingAt)}
                  </Typography>
                </Box>
                {subscription.status !== 'cancelled' && subscription.status !== 'non_renewing' && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<SyncDisabledIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => setCancelDialogOpen(true)}
                    disabled={actionLoading}
                    sx={{ ...TYPE.body, mt: 1, px: 0, color: 'text.secondary', whiteSpace: 'nowrap', textTransform: 'none', '&:hover': { color: 'warning.main', bgcolor: 'transparent' } }}
                  >
                    Turn off auto-renew
                  </Button>
                )}
                {subscription.status === 'non_renewing' && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<RefreshIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={handleReactivateSubscription}
                    disabled={actionLoading}
                    sx={{ ...TYPE.body, mt: 1, px: 0, color: 'text.secondary', whiteSpace: 'nowrap', textTransform: 'none', '&:hover': { color: 'success.main', bgcolor: 'transparent' } }}
                  >
                    Reactivate
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          {/* Status warnings */}
          {subscription.status === 'in_trial' && (
            <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: 3 }}>
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ borderRadius: 2 }}
                action={
                  isTenantAdmin ? (
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<CreditCardIcon sx={{ fontSize: '16px !important' }} />}
                      onClick={handleUpdatePaymentMethod}
                      disabled={actionLoading}
                      sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600 }}
                    >
                      Add payment method
                    </Button>
                  ) : undefined
                }
              >
                {subscription.trialEnd ? (
                  <>Your free trial ends on <strong>{formatDate(subscription.trialEnd)}</strong>.</>
                ) : (
                  'Your organization is on a free trial.'
                )}{' '}
                Add a payment method to keep access; without one, the subscription is
                cancelled when the trial ends.
              </Alert>
            </Box>
          )}
          {subscription.status === 'non_renewing' && (
            <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: 3 }}>
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                Your subscription will not renew. Your organization keeps full access until{' '}
                <strong>{formatDate(subscription.currentTermEnd)}</strong>.
              </Alert>
            </Box>
          )}
          {subscription.status === 'cancelled' && (
            <Box sx={{ px: { xs: 2.5, sm: 4 }, pb: 3 }}>
              <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                {/* Chargebee keeps trial_end forever, so only treat this as a trial expiry when
                    the subscription never ran a paid term past the trial. */}
                {subscription.trialEnd &&
                (!subscription.currentTermEnd ||
                  new Date(subscription.currentTermEnd) <= new Date(subscription.trialEnd))
                  ? 'Your free trial has ended. Choose a plan below and enter payment details to restore access.'
                  : 'This subscription expired. Choose a plan below to restore access.'}
              </Alert>
            </Box>
          )}

          {/* Card footer action */}
          {subscription.status !== 'cancelled' && isTenantAdmin && (
            <>
              <Divider />
              <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<CreditCardIcon sx={{ fontSize: '16px !important' }} />}
                  onClick={handleUpdatePaymentMethod}
                  disabled={actionLoading}
                  sx={{ ...TYPE.body, textTransform: 'none', color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'transparent' } }}
                >
                  Update billing information
                </Button>
              </Box>
            </>
          )}
        </Box>
      ) : (
        /* ── Empty state — no subscription ── */
        <Box
          sx={{
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4 },
          }}
        >
          <Typography sx={{ ...TYPE.overline, color: 'text.secondary', display: 'block', mb: 0.75 }}>
            No organization plan
          </Typography>
          <Typography sx={{ ...TYPE.display, color: 'text.primary', mb: 1 }}>
            Get started with CPAx Academy
          </Typography>
          <Typography sx={{ ...TYPE.body, color: 'text.secondary', maxWidth: '60ch' }}>
            Choose a plan below to activate your organization&rsquo;s subscription.
          </Typography>
        </Box>
      )}

      {/* ── Add-on Seats Panel (tenant-only) ── */}
      {addonSeatSummaries.length > 0 && (
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              px: { xs: 2.5, sm: 4 },
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIndIcon sx={{ fontSize: 20, color: 'primary.main' }} />
              <Typography sx={{ ...TYPE.subheading }}>
                Module Seats
              </Typography>
              <Tooltip
                title="Each purchased module has a fixed seat count. Assign seats to users in the User Management page."
                arrow
                placement="right"
              >
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
            </Box>
            {isTenantAdmin && (
              <Button
                component={Link}
                to="/app/tenant-admin/users"
                variant="outlined"
                size="small"
                startIcon={<ManageAccountsIcon />}
              >
                Manage seats
              </Button>
            )}
          </Box>
          <Box sx={{ px: { xs: 2.5, sm: 4 }, py: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {addonSeatSummaries.map((addon) => {
              const pct = addon.purchasedQuantity === 0 ? 0 : (addon.assignedCount / addon.purchasedQuantity) * 100;
              const isFull = addon.availableCount === 0;
              const isNearLimit = !isFull && pct >= 80;
              const seatToken = isFull ? 'attention' : isNearLimit ? 'atRisk' : 'enrolled';
              return (
                <Box
                  key={addon.addonId}
                  sx={{
                    minWidth: 220,
                    flex: '1 1 220px',
                    maxWidth: 320,
                    p: 1.75,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: isFull ? 'error.light' : 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1 }}>
                    <Typography sx={{ ...TYPE.bodyStrong }} noWrap>
                      {addon.addonDisplayName}
                    </Typography>
                    <Chip
                      label={isFull ? 'Full' : `${addon.availableCount} available`}
                      size="small"
                      sx={{
                        ...TYPE.caption,
                        fontWeight: 600,
                        height: 22,
                        flexShrink: 0,
                        bgcolor: `status.${seatToken}.bg`,
                        color: `status.${seatToken}.fg`,
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    color={isFull ? 'error' : isNearLimit ? 'warning' : 'primary'}
                    sx={{ mb: 0.75 }}
                  />
                  <Typography sx={{ ...TYPE.caption, color: 'text.secondary' }}>
                    {addon.assignedCount} of {addon.purchasedQuantity} seats used
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Plan Selection / Module Manager ── */}
      <Box>
        <Typography
          sx={{
            ...TYPE.subheading,
            mb: subscription && subscription.status !== 'cancelled' ? 0.5 : 2.5,
          }}
        >
          {subscription && subscription.status !== 'cancelled'
            ? isTenantAdmin
              ? 'Modules & seats'
              : 'Modules'
            : 'Choose a plan'}
        </Typography>
        {subscription && subscription.status !== 'cancelled' && (
          <Typography sx={{ ...TYPE.body, color: 'text.secondary', mb: 2.5, maxWidth: '60ch' }}>
            {isTenantAdmin
              ? 'Add modules or adjust seat counts. Changes go through a secure checkout.'
              : 'Add modules. Changes are prorated and go through a secure checkout.'}
          </Typography>
        )}
        {subscription && subscription.status !== 'cancelled' ? (
          isTenantAdmin ? (
            <ActiveModuleManager
              addonType="enterprise"
              activeAddonIds={subscription.activeAddonIds ?? []}
              currentSeatCounts={subscription.addonQuantities ?? {}}
              seatSummaries={addonSeatSummaries}
              onApplyChanges={handleModuleChanges}
              isApplying={actionLoading}
            />
          ) : (
            <ActiveModuleManager
              addonType="individual"
              activeAddonIds={subscription.activeAddonIds ?? []}
              onApplyChanges={handleModuleChanges}
              isApplying={actionLoading}
            />
          )
        ) : isTenantAdmin ? (
          <>
            <BusinessPlanSelectionPanel onContinue={handleBusinessCheckout} />
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setSalesDialogOpen(true)}
                sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.875rem' }}
              >
                Prefer to talk to sales?
              </Button>
            </Box>
          </>
        ) : (
          <IndividualPlanSelectionPanel onContinue={handleIndividualCheckout} />
        )}
      </Box>

      {/* ── Billing History ── */}
      <Box>
        <Typography sx={{ ...TYPE.subheading, mb: 2 }}>
          Billing history
        </Typography>
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {invoices.length > 0 ? (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'surface.subtle' }}>
                    <TableCell sx={{ ...TYPE.overline, color: 'text.secondary' }}>Date</TableCell>
                    <TableCell sx={{ ...TYPE.overline, color: 'text.secondary' }}>Status</TableCell>
                    <TableCell align="right" sx={{ ...TYPE.overline, color: 'text.secondary' }}>Amount</TableCell>
                    <TableCell sx={{ ...TYPE.overline, color: 'text.secondary' }}>Due Date</TableCell>
                    <TableCell align="center" sx={{ ...TYPE.overline, color: 'text.secondary' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ ...TYPE.body }}>{formatDate(invoice.date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={invoice.status}
                          size="small"
                          sx={[
                            subscriptionChipSx(invoice.status === 'paid' ? 'success' : 'neutral'),
                            { ...TYPE.caption, fontWeight: 600, height: 22, textTransform: 'capitalize' },
                          ]}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ ...TYPE.bodyStrong, fontVariantNumeric: 'tabular-nums' }}>
                        {formatCurrency(invoice.total, invoice.currency)}
                      </TableCell>
                      <TableCell sx={{ ...TYPE.body }}>{formatDate(invoice.dueDate)}</TableCell>
                      <TableCell align="center">
                        {invoice.hostedInvoiceUrl && (
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<OpenInNewIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={() => window.open(invoice.hostedInvoiceUrl, '_blank')}
                            sx={{ ...TYPE.body, textTransform: 'none' }}
                          >
                            View
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ ...TYPE.body, color: 'text.secondary', textAlign: 'center', py: 6 }}>
              No invoices found
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Turn Off Auto-Renew Dialog ── */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle sx={TYPE.subheading}>Turn off auto-renew?</DialogTitle>
        <DialogContent>
          <Typography sx={{ ...TYPE.body, mb: 2 }}>
            Your organization keeps full access until{' '}
            <strong>{formatDate(subscription?.currentTermEnd ?? subscription?.nextBillingAt)}</strong>.
            After that, the subscription expires and you won&rsquo;t be charged again.
          </Typography>
          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'tint.primarySoft' }}>
            <Typography sx={{ ...TYPE.body }}>
              Need to remove a module or request a prorated refund? Email{' '}
              <a
                href="mailto:hello@cpacrossings.com?subject=Cpax%20Academy%20organization%20subscription%20change"
                style={{ color: 'inherit', fontWeight: 600 }}
              >
                hello@cpacrossings.com
              </a>
              .
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelSubscription}
            color="warning"
            variant="text"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Turn off auto-renew'}
          </Button>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            variant="contained"
            color="primary"
            disabled={actionLoading}
            autoFocus
          >
            Keep auto-renew
          </Button>
        </DialogActions>
      </Dialog>

      <TalkToSalesDialog open={salesDialogOpen} onClose={() => setSalesDialogOpen(false)} />
    </Stack>
  );
}
